import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningLab } from '../../contexts/LearningLabContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { getLabMissionTourSlugs } from '../../../shared/onboarding/tourCatalog';
import { findResumableLabMission } from '../../../shared/onboarding/missionSessionStorage';
import type { CoordinatorLabTourSlug } from '../../../shared/onboarding/coordinatorLearningLabTours';
import { ensureMissionRouteForResumedMission } from '../../utils/missionRouteEnsure';

/**
 * Restarts an in-progress lab mission after tour-driven navigation that remounted
 * the app (legacy full reload) or any interruption that left a session snapshot.
 */
export const LearningLabMissionAutoResume: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { active } = useLearningLab();
  const { state, startTour, config, getAllTours } = useOnboarding();
  const resumeAttemptRef = useRef<string | null>(null);

  useEffect(() => {
    resumeAttemptRef.current = null;
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!config.enabled || !active || !user?.id || state.isRunning) {
      return undefined;
    }

    const slug = findResumableLabMission(user.id, getLabMissionTourSlugs());
    if (!slug) {
      return undefined;
    }

    const attemptKey = `${slug}:${location.pathname}${location.search}`;
    if (resumeAttemptRef.current === attemptKey) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      resumeAttemptRef.current = attemptKey;
      void (async () => {
        const flow = getAllTours().find((t) => t.id === slug);
        if (user?.id && flow?.steps?.length) {
          await ensureMissionRouteForResumedMission(
            user.id,
            slug as CoordinatorLabTourSlug,
            flow.steps,
          );
        }
        await startTour(slug);
      })();
    }, 800);

    return () => window.clearTimeout(timer);
  }, [
    active,
    config.enabled,
    getAllTours,
    location.pathname,
    location.search,
    startTour,
    state.isRunning,
    user?.id,
  ]);

  return null;
};

export default LearningLabMissionAutoResume;
